/**
 * Main component of the create enterprise account page
 * @author Housefolios
 * @created 2021-10-05 11:07:32
 * @modified 2025-05-07 19:08:07
 * @modifiedBy David Helmick
 */

import React, { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client/react'
// Centralized lodash imports - much cleaner!
import { map, includes, upperFirst } from '@/utils/lodash'

import {
  Grid,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  Box,
} from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Check } from '@mui/icons-material'

import { ValidatorForm } from '@/housefolios-components/shared/muiFormValidator'
import {
  pageShellWrapperSx,
  pageShellMainSx,
  pageShellContentSx,
} from '@/layout-blueprints/chromeSx'
import { innerContentMainSx } from '@/layout-blueprints/chromeComponentSx'

// Import shared components
import EmailInput from '../../FormComponents/EmailInput'
import PasswordInput from '../../FormComponents/PasswordInput'
import NameInput from '../../FormComponents/NameInput'
import PhoneInput from '../../FormComponents/PhoneInput'

// Import shared hooks
import { useUrlParams } from '@/hooks/useUrlParams'
import { useSignupMutation } from '@/hooks/useSignupMutation'

// Import HOC
import withLoading from '../../Session/withLoading'

import { GET_SUBSCRIPTION_PLAN } from './queries'
import PaymentForm from '../PaymentForm'
import {
  GET_AFFILIATE,
  VALIDATE_AFFILIATE,
} from '../CreateAccountCard/queries'
import { useParams } from 'react-router'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import FormSection from '../Shared/FormSection'
import PricingSection from './PricingSection'
import AffiliateSection from '../Shared/AffiliateSection'

function CreateEnterpriseAccountComponent({ setLoader  }) {
  const { planName = 'enterprise-affiliate' } = useParams()
  const [activeTab1, setActiveTab1] = useState(planName)
  const [affiliate, setAffiliate] = useState('')
  const [affiliateValid, setAffiliateValid] = useState(false)
  const [agree, setAgree] = useState(false)
  const [token, setToken] = useState('')
  const [resetToken, setResetToken] = useState(() => null)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [password, setPassword] = useState('')
  const [passwordMask, setPasswordMask] = useState(true)
  const [loader, setLocalLoader] = useState(false)
  const [affiliatePrice, setAffiliatePrice] = useState(0)
  const [affiliateBilling, setAffiliateBilling] = useState('monthly')
  const [tokenLoading, setTokenLoading] = useState(false)

  const {
    hfpid,
    refOrg,
    source: urlSource,
    affiliate: urlAffiliate,
  } = useUrlParams()

  console.log('urlAffiliate:', urlAffiliate)

  // Enterprise-specific source validation
  const rockstarReferrer = ['https://go.housefolios.com/rerockstars']
  const myndReferrer = [
    'https://housefolios.com/mynd/',
    'https://www.housefolios.com/mynd/',
  ]

  const isRockstar =
    urlSource === 'rerockstars' &&
    (includes(rockstarReferrer, document.referrer) ||
      includes(
        map(rockstarReferrer, (ref) => ref + '/'),
        document.referrer,
      )) &&
    activeTab1 === 'investor-yearly'

  const isMynd =
    urlSource === 'mynd' &&
    (includes(myndReferrer, document.referrer) ||
      includes(
        map(myndReferrer, (ref) => ref + '/'),
        document.referrer,
      ))
  // let isPaul = false
  // //if (source === "paulmccomas" && (includes(paulReferrer, document.referrer) || includes(map(paulReferrer, ref => ref+'/'), document.referrer)))
  // if (source === 'paulmccomas') {
  //   isPaul = true
  //   hfpid = 'A1'
  // }

  // let isGorilla = false
  // if (source === 'gorillacapital') {
  //   isGorilla = true
  //   hfpid = 'A2'
  // }

  // let isInvestarters = false
  // if (source === 'investarters') {
  //   isInvestarters = true
  //   hfpid = 'A3'
  // }
  // //let thinkRealtyReferrer = ["https://housefolios.com/thinkrealty/", "https://www.housefolios.com/thinkrealty/"]
  // let isThinkRealty = false
  // if (source === 'thinkrealty') {
  //   isThinkRealty = true
  //   hfpid = 'A4'
  // }
  // let isFlipology = false
  // if (source === 'flipology') {
  //   isFlipology = true
  //   hfpid = 'A5'
  // }
  // let isFlippingAmerica = false
  // if (source === 'flippingamerica') {
  //   isFlippingAmerica = true
  //   hfpid = 'A6'
  // }
  // let isPrivateMoneyClub = false
  // if (source === 'privatemoneyclub') {
  //   isPrivateMoneyClub = true
  //   hfpid = '76'
  // }
  // let isSyndicatedCapital = false
  // if (source === 'syndicatedcapital') {
  //   isSyndicatedCapital = true
  //   hfpid = 'A8'
  // }
  // let isTwoGuysTakeOnRealEstate = false
  // if (source === 'twoguystakeonrealestate') {
  //   isTwoGuysTakeOnRealEstate = true
  //   hfpid = 'A9'
  // }
  const isPaul = urlSource === 'paulmccomas'
  const isGorilla = urlSource === 'gorillacapital'
  const isInvestarters = urlSource === 'investarters'
  const isThinkRealty = urlSource === 'thinkrealty'
  const isFlipology = urlSource === 'flipology'
  const isFlippingAmerica = urlSource === 'flippingamerica'
  const isPrivateMoneyClub = urlSource === 'privatemoneyclub'
  const isSyndicatedCapital = urlSource === 'syndicatedcapital'
  const isTwoGuysTakeOnRealEstate =
    urlSource === 'twoguystakeonrealestate'

  // If none of the sources booleans are true the source isn't valid
  const source =
    !isRockstar && !isMynd && !isPaul && !isGorilla && !isInvestarters
      ? null
      : urlSource

  const { loading, data, error } = useQuery(GET_SUBSCRIPTION_PLAN, {
    variables: { subscriptionPlan: activeTab1 },
    errorPolicy: 'ignore',
  })

  const { subscriptionPlan = {} } = data || {}
  const planID = subscriptionPlan.planId

  // Add affiliate validation mutation
  const [validateAffiliateMutation] = useMutation(VALIDATE_AFFILIATE)

  // Validate affiliate code when it changes
  useEffect(() => {
    console.log('[Affiliate Validation] useEffect triggered', {
      affiliate,
      activeTab1,
      affiliateBilling,
    })

    const validateAffiliateCode = async () => {
      console.log(
        '[Affiliate Validation] validateAffiliateCode called with:',
        affiliate,
      )

      if (!affiliate || affiliate.trim() === '') {
        setAffiliateValid(false)
        setAffiliatePrice(subscriptionPlan.price || 0)
        setAffiliateBilling(
          subscriptionPlan.paymentInterval || 'monthly',
        )
        console.log(
          '[Affiliate Validation] Empty code, resetting to default plan:',
          subscriptionPlan.price,
          subscriptionPlan.paymentInterval,
        )
        return
      }

      try {
        const variables = {
          code: affiliate.trim(),
          planName: activeTab1,
          paymentInterval: affiliateBilling === 'monthly' ? 1 : 2,
        }
        console.log(
          '[Affiliate Validation] About to call mutation with:',
          variables,
        )
        const { data } = await validateAffiliateMutation({
          variables,
        })
        console.log('[Affiliate Validation] Mutation response:', data)

        if (data?.validateAffiliate?.valid) {
          setAffiliateValid(true)
          setAffiliatePrice(data.validateAffiliate.price)
          setAffiliateBilling(data.validateAffiliate.billing)
          console.log(
            '[Affiliate Validation] Valid! Price:',
            data.validateAffiliate.price,
            'Billing:',
            data.validateAffiliate.billing,
          )
        } else {
          setAffiliateValid(false)
          setAffiliatePrice(subscriptionPlan.price || 0)
          setAffiliateBilling(
            subscriptionPlan.paymentInterval || 'monthly',
          )
          console.log(
            '[Affiliate Validation] Invalid code, resetting to default plan:',
            subscriptionPlan.price,
            subscriptionPlan.paymentInterval,
          )
        }
      } catch (error) {
        setAffiliateValid(false)
        setAffiliatePrice(subscriptionPlan.price || 0)
        setAffiliateBilling(
          subscriptionPlan.paymentInterval || 'monthly',
        )
        console.log('[Affiliate Validation] Error:', error)
      }
    }

    const timeoutId = setTimeout(validateAffiliateCode, 500)
    return () => clearTimeout(timeoutId)
  }, [
    affiliate,
    activeTab1,
    affiliateBilling,
    validateAffiliateMutation,
    subscriptionPlan,
  ])

  // Auto-populate affiliate code from URL param if present
  useEffect(() => {
    if (
      typeof urlAffiliate === 'string' &&
      urlAffiliate &&
      urlAffiliate !== affiliate
    ) {
      console.log('Setting affiliate from URL:', urlAffiliate)
      setAffiliate(urlAffiliate)
    }
  }, [urlAffiliate, affiliate])

  // Debug validation trigger
  useEffect(() => {
    console.log('Validating affiliate:', affiliate)
  }, [affiliate])

  // Debug button state
  useEffect(() => {
    const buttonDisabled =
      !token || !agree || !affiliateValid || tokenLoading
    console.log('[BUTTON STATE] Button disabled status changed:', {
      buttonDisabled,
      reasons: {
        noToken: !token,
        notAgreed: !agree,
        affiliateInvalid: !affiliateValid,
        tokenLoading,
      },
      values: {
        hasToken: !!token,
        tokenType: typeof token,
        agree,
        affiliateValid,
        tokenLoading,
        affiliate,
        email,
      },
    })
  }, [token, agree, affiliateValid, tokenLoading, affiliate, email])

  // Reset token and clear errors when email changes (new signup attempt)
  useEffect(() => {
    if (email && email.length > 0) {
      console.log(
        '[EMAIL CHANGE] Email changed, clearing token and errors:',
        email,
      )
      setToken('')
      setTokenLoading(false)
      // Clear any error messages
      const errorElement = document.getElementById('card-errors')
      if (errorElement) {
        errorElement.textContent = ''
      }
    }
  }, [email])

  const userInput = useMemo(
    () => ({
      profile: {
        firstname: firstName,
        lastname: lastName,
      },
      email: email.toLowerCase(),
      organization: firstName + lastName,
      phone: phoneNumber,
      stripePlanId: planID,
      stripeToken: token,
      password: password,
      referredByDetails: hfpid
        ? {
            affiliateId: hfpid,
            saleDate: new Date(),
            productSelected: 'enterprise-affiliate',
            productTotalPrice: affiliatePrice / 100,
            couponUsed: null,
            active: true,
          }
        : null,
      refOrg: refOrg || null,
      source,
      affiliatePlan: affiliate,
    }),
    [
      firstName,
      lastName,
      email,
      phoneNumber,
      planID,
      token,
      password,
      hfpid,
      affiliatePrice,
      refOrg,
      source,
      affiliate,
    ],
  )

  const { checkSignup, signUp, signUpError } =
    useSignupMutation(userInput)

  // Reset all states that could keep the button disabled
  const resetFormStates = () => {
    console.log('[RESET] Resetting form states...')
    setToken('')
    setTokenLoading(false)
    setLocalLoader(false)
    // Don't reset affiliate validation or agree checkbox as user may want to keep those
    if (resetToken) {
      resetToken()
    }
    // Clear any error messages
    const errorElement = document.getElementById('card-errors')
    if (errorElement) {
      errorElement.textContent = ''
    }
  }

  const handleSubmit = async () => {
    console.log('[SIGNUP] handleSubmit called with:', {
      token: !!token,
      agree,
      affiliateValid,
      email,
      firstName,
      lastName,
      phoneNumber,
      planID,
      hfpid,
      affiliatePrice,
    })

    if (!token || !agree || !affiliateValid) {
      console.log('[SIGNUP] Validation failed:', {
        hasToken: !!token,
        agree,
        affiliateValid,
      })
      return
    }

    setLocalLoader(true)
    const productChoice = 'enterprise-affiliate'

    console.log('[SIGNUP] Starting signup process')
    console.log('[SIGNUP] User input object:', userInput)
    console.log('[SIGNUP] Token info:', {
      hasToken: !!token,
      tokenType: typeof token,
      tokenValue: token,
      tokenLength: typeof token === 'string' ? token.length : 'N/A',
    })

    try {
      console.log('[SIGNUP] Calling checkSignup...')
      await checkSignup()
      console.log('[SIGNUP] checkSignup completed, calling signUp...')

      const { data } = await signUp()
      console.log('[SIGNUP] signUp completed with data:', data)

      console.log(
        'trackSignup args:',
        typeof 'enterprise-affiliate',
        typeof email,
        typeof hfpid,
        hfpid,
        typeof affiliatePrice,
        affiliatePrice,
      )

      console.log('[SIGNUP] Setting localStorage and reloading...', {
        orgId: data.signUp.orgId,
        hasToken: !!data.signUp.token,
      })
      localStorage.setItem(`buyBox-${data.signUp.orgId}`, 'true')
      localStorage.setItem('token', data.signUp.token)
      window.location.reload()
    } catch (error) {
      console.log('[SIGNUP] Error occurred during signup:', error)
      console.log('[SIGNUP] Error message:', error.message)
      console.log('[SIGNUP] Error stack:', error.stack)
      console.log(
        '[SIGNUP] Full error object:',
        JSON.stringify(error, null, 2),
      )

      var errorElement = document.getElementById('card-errors')
      if (error && errorElement) {
        const errorMessage =
          error.message.slice(0, 21) === 'GraphQL error: Error:'
            ? error.message.slice(21)
            : error.message
        console.log(
          '[SIGNUP] Setting error message to:',
          errorMessage,
        )
        errorElement.textContent = errorMessage
      }
      console.log('[SIGNUP] Calling resetFormStates...')
      resetFormStates()
    } finally {
      console.log('[SIGNUP] Finally block - setting loader to false')
      setLocalLoader(false)
    }
  }

  const createToken = async (stripe, cardElement, nameOnCard) => {
    console.log('[createToken] Called with:', {
      nameOnCard,
      hasStripe: !!stripe,
      hasCardElement: !!cardElement,
    })

    if (!nameOnCard) {
      console.log('[createToken] No nameOnCard, clearing token')
      setToken('')
    } else {
      console.log('[createToken] Creating token for:', nameOnCard)
      setTokenLoading(true)

      try {
        const tokenResult = await stripe.createToken(cardElement, {
          name: nameOnCard,
        })

        console.log('[createToken] Stripe createToken result:', {
          hasError: !!tokenResult.error,
          hasToken: !!tokenResult.token,
          error: tokenResult.error,
          tokenId: tokenResult.token?.id,
        })

        var errorElement = document.getElementById('card-errors')
        if (tokenResult.error) {
          console.log(
            '[createToken] Stripe error:',
            tokenResult.error,
          )
          setToken('')
          if (errorElement)
            errorElement.textContent = tokenResult.error.message
        } else {
          console.log('[createToken] Success! Token created:', {
            id: tokenResult.token.id,
            card: tokenResult.token.card,
          })
          setToken(tokenResult.token)
          if (errorElement) errorElement.textContent = null
        }
      } catch (stripeError) {
        console.log(
          '[createToken] Exception during stripe.createToken:',
          stripeError,
        )
        setToken('')
        var errorElement = document.getElementById('card-errors')
        if (errorElement)
          errorElement.textContent =
            stripeError.message || 'An error occurred'
      } finally {
        console.log(
          '[createToken] Finished, setting tokenLoading to false',
        )
        setTokenLoading(false)
      }
    }
  }

  return (
    <>
      <Box sx={{ ...pageShellWrapperSx }}>
        <Box sx={pageShellMainSx}>
          <Box sx={pageShellContentSx}>
            <Box sx={innerContentMainSx}>
              <Box
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ position: "relative", zIndex: "7", width: "100%" }}>
                  <ValidatorForm onSubmit={handleSubmit}>
                    <BlockUi
                      blocking={loader}
                      loader={
                        <PacmanLoader
                          color={'var(--primary)'}
                          loading={loader}
                        />
                      }
                      message={
                        <Box sx={{ color: '#368cff' }}>
                          Creating Account
                        </Box>
                      }
                    >
                      <Grid
                        container
                        spacing={0}

                        sx={{ minHeight: '100vh' }}
                      >
                        <Grid
                          size={12}

                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Grid sx={{ mx: 'auto' }}>
                            <Card
                              style={{
                                padding: 30,
                                maxWidth: 600,
                              }}
                            >
                              <Grid container direction="column">
                                <FormSection
                                  email={email}
                                  setEmail={setEmail}
                                  password={password}
                                  setPassword={setPassword}
                                  passwordMask={passwordMask}
                                  setPasswordMask={setPasswordMask}
                                  firstName={firstName}
                                  setFirstName={setFirstName}
                                  lastName={lastName}
                                  setLastName={setLastName}
                                  phoneNumber={phoneNumber}
                                  setPhoneNumber={setPhoneNumber}
                                  signUpError={signUpError}
                                />
                                <Divider
                                  style={{
                                    marginTop: 20,
                                    marginBottom: 10,
                                  }}
                                />
                                <AffiliateSection
                                  affiliate={affiliate}
                                  setAffiliate={setAffiliate}
                                  affiliateValid={affiliateValid}
                                  setAffiliateValid={
                                    setAffiliateValid
                                  }
                                  affiliatePrice={affiliatePrice}
                                  affiliateBilling={affiliateBilling}
                                  isPaul={isPaul}
                                  isGorilla={isGorilla}
                                  isInvestarters={isInvestarters}
                                />
                                <Divider
                                  style={{
                                    marginTop: 20,
                                    marginBottom: 10,
                                  }}
                                />
                                <PricingSection
                                  createToken={createToken}
                                  setResetToken={setResetToken}
                                  agree={agree}
                                  setAgree={setAgree}
                                  tokenLoading={tokenLoading}
                                />
                                <Grid
                                  size={12}
                                  style={{
                                    marginTop: 25,
                                  }}
                                >
                                  <Button
                                    fullWidth={true}
                                    variant="contained"
                                    type="submit"
                                    disabled={
                                      !token ||
                                      !agree ||
                                      !affiliateValid ||
                                      tokenLoading
                                    }
                                    color="primary"
                                  >
                                    <Typography>
                                      {tokenLoading
                                        ? 'Processing...'
                                        : 'BEGIN AFFILIATE PLAN'}
                                    </Typography>
                                  </Button>
                                </Grid>
                                <Grid
                                  size={12}
                                  style={{
                                    marginTop: 25,
                                  }}
                                >
                                  <Link
                                    href="/signin"
                                    underline="hover"
                                    style={{
                                      color: '#04a3e3',
                                    }}
                                    target="_blank"
                                  >
                                    Already have an account?
                                  </Link>
                                </Grid>
                              </Grid>
                            </Card>
                          </Grid>
                        </Grid>
                      </Grid>
                    </BlockUi>
                  </ValidatorForm>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default withLoading(CreateEnterpriseAccountComponent)
